import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ChannelApiResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import axios from 'axios';
import type { ReadRequestOptions } from '../requestOptions';

/**
 * One channel-ledger entry (settlement line) behind an order's summary. `fee_type` is the canonical
 * classification; `amount` is a signed fixed-precision string; `external_id` carries the vendor
 * component discriminator (e.g. "{key}::Commission").
 */
export interface OrderLedgerEntry {
  id: number;
  fee_type: string;
  /** The channel's native line type (e.g. "COMMISSION_VAT", "Product Price"), if any. */
  description: string | null;
  /** SKU / item / order-line the line applies to, if the channel provides one. */
  item_ref: string | null;
  payment_state: string | null;
  external_id: string;
  amount: string;
  currency: string;
  occurred_at: string | null;
  statement_id: number | null;
}

/**
 * Fetches the individual ledger entries for one Astur order (the Settlement tab drill-down). A 404
 * (no settlement for this order) resolves to an empty list rather than an error.
 */
export async function getOrderLedgerEntries(
  scope: RequestScope,
  args: { subscriptionId: number; asturOrderId: number },
  options: ReadRequestOptions
): Promise<ChannelApiResponse<OrderLedgerEntry[]>> {
  try {
    if (!args.subscriptionId || args.subscriptionId < 1) {
      throw new Error('Valid subscriptionId is required');
    }
    if (!args.asturOrderId || args.asturOrderId < 1) {
      throw new Error('Valid asturOrderId is required');
    }

    const res = await scopedChannelApi(scope).get<OrderLedgerEntry[]>(`astur/integrations/channel/ledger/order-entries/${args.asturOrderId}`, {
      ...options,
      headers: { 'x-astur-subscription-id': args.subscriptionId.toString() },
    });
    return { data: res.data, status: res.status };
  } catch (e) {
    if (axios.isAxiosError(e) && e.response?.status === 404) {
      return { data: [], status: 404 };
    }
    return utils.error.handleAxiosError(e);
  }
}
